import React, { useState, useEffect, useCallback } from 'react';
import { AxiosResponse } from 'axios';
import {
  Modal, Button, Table, Form, Row, Col, Alert,
} from 'react-bootstrap';
import moment from 'moment';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import ViewVisitorRequest from './ViewVisitorRequest';
import MarkInModule from './MarkInModule';
import MarkOutModule from './MarkOutModule';
import { showError } from '../../../../core/alert/sweetAlertConfig';

// Base type from your MarkInModule; extend with check states for safety
import type { VisitorRequestDTO as BaseVisitorRequestDTO } from './MarkInModule';
type VisitorRequestDTO = BaseVisitorRequestDTO & {
  checkInStatus?: 'NOT_CHECKED_IN' | 'PARTIALLY_CHECKED_IN' | 'FULLY_CHECKED_IN' | null;
  checkOutStatus?: 'NOT_CHECKED_OUT' | 'PARTIALLY_CHECKED_OUT' | 'FULLY_CHECKED_OUT' | null;
};

interface FinalApprovedModuleProps {
  show: boolean;
  onHide: () => void;
}

const statusColors: { [key: string]: string } = {
  FINAL_APPROVED: 'badge bg-info',
};

const PAGE_SIZE = 15;

// Status constants
const NOT_CHECKED_IN = 'NOT_CHECKED_IN';
const PARTIALLY_CHECKED_IN = 'PARTIALLY_CHECKED_IN';
const FULLY_CHECKED_IN = 'FULLY_CHECKED_IN';

const NOT_CHECKED_OUT = 'NOT_CHECKED_OUT';
const PARTIALLY_CHECKED_OUT = 'PARTIALLY_CHECKED_OUT';
const FULLY_CHECKED_OUT = 'FULLY_CHECKED_OUT';

const FinalApprovedModule: React.FC<FinalApprovedModuleProps> = ({ show, onHide }) => {
  const [visitorRequests, setVisitorRequests] = useState<VisitorRequestDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [pageNo, setPageNo] = useState(1);
  const [total, setTotal] = useState(0);
  const [dateFilter, setDateFilter] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedVisitorRequestId, setSelectedVisitorRequestId] = useState<number | null>(null);

  const [showMarkInModal, setShowMarkInModal] = useState(false);
  const [showMarkOutModal, setShowMarkOutModal] = useState(false);

  const fetchVisitorRequests = useCallback(
    async (page: number) => {
      setLoading(true);
      setErrorText(null);
      try {
        const payload = { filterType: 'STATUS', filterValue: 'FINAL_APPROVED', date: dateFilter };
        const res: AxiosResponse = await apiDefinitions.getVisitors(page.toString(), payload);

        // API shape: { status: "SUCCESS", data: [...], total }
        if (res.status === 200 && (res.data?.status === 'SUCCESS' || res.data?.status === 'S1000')) {
          const rows: VisitorRequestDTO[] = Array.isArray(res.data?.data) ? res.data.data : [];
          setVisitorRequests(rows);
          setTotal(typeof res.data?.total === 'number' ? res.data.total : 0);
        } else {
          const msg = res.data?.statusDescription || 'Failed to fetch final approved visitor requests';
          setErrorText(msg);
          showError(msg);
          setVisitorRequests([]);
          setTotal(0);
        }
      } catch (err: any) {
        const msg =
          err?.response?.data?.statusDescription ||
          err?.message ||
          'Failed to fetch final approved visitor requests. Please try again.';
        setErrorText(msg);
        showError(msg);
        setVisitorRequests([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    },
    [dateFilter]
  );

  useEffect(() => {
    if (show) {
      setPageNo(1);
      fetchVisitorRequests(1);
    }
  }, [show, dateFilter, fetchVisitorRequests]);

  useEffect(() => {
    if (show) {
      fetchVisitorRequests(pageNo);
    }
  }, [pageNo, show, fetchVisitorRequests]);

  const handlePageChange = (newPage: number) => {
    const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (newPage > 0 && newPage <= maxPage) setPageNo(newPage);
  };

  const handleDateFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDateFilter(e.target.value);
    setPageNo(1);
  };

  const handleViewClick = (visitorRequestId: number) => {
    setSelectedVisitorRequestId(visitorRequestId);
    setShowViewModal(true);
  };

  const handleMarkInClick = (visitorRequestId: number) => {
    setSelectedVisitorRequestId(visitorRequestId);
    setShowMarkInModal(true);
  };

  const handleMarkOutClick = (visitorRequestId: number) => {
    setSelectedVisitorRequestId(visitorRequestId);
    setShowMarkOutModal(true);
  };

  const handleMarkChangeSuccess = () => {
    fetchVisitorRequests(pageNo);
  };

  // Normalize nulls to NOT_* states for logic and display
  const normIn = (r: VisitorRequestDTO) => r.checkInStatus ?? NOT_CHECKED_IN;
  const normOut = (r: VisitorRequestDTO) => r.checkOutStatus ?? NOT_CHECKED_OUT;

  // Visibility rules you asked for:
  // - Mark In: NOT_CHECKED_IN, PARTIALLY_CHECKED_IN
  // - Mark Out: PARTIALLY_CHECKED_IN, PARTIALLY_CHECKED_OUT, FULLY_CHECKED_IN
  // - When FULLY_CHECKED_OUT: show no buttons at all
  const canShowAnyButtons = (r: VisitorRequestDTO) => normOut(r) !== FULLY_CHECKED_OUT;

  const canShowMarkIn = (r: VisitorRequestDTO) => {
    const ci = normIn(r);
    return ci === NOT_CHECKED_IN || ci === PARTIALLY_CHECKED_IN;
  };

  const canShowMarkOut = (r: VisitorRequestDTO) => {
    const ci = normIn(r);
    const co = normOut(r);
    return (
      ci === PARTIALLY_CHECKED_IN ||
      ci === FULLY_CHECKED_IN ||
      co === PARTIALLY_CHECKED_OUT
    );
  };

  const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title>Final Approved Visitor Requests</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Label>Filter by Date</Form.Label>
              <Form.Control
                type="date"
                value={dateFilter}
                onChange={handleDateFilterChange}
                disabled={loading}
              />
            </Col>
          </Row>

          {loading && <div className="text-center">Loading...</div>}
          {errorText && <Alert variant="danger">{errorText}</Alert>}
          {!loading && !errorText && visitorRequests.length === 0 && (
            <Alert variant="info">No final approved visitor requests found.</Alert>
          )}

          {!loading && !errorText && visitorRequests.length > 0 && (
            <div className="table-responsive">
              <Table className="table text-nowrap">
                <thead className="table-primary">
                  <tr>
                    <th>Company</th>
                    <th>Arrival Time</th>
                    <th>Status</th>
                    <th>Check-In</th>
                    <th>Check-Out</th>
                    <th>Created Date</th>
                    <th style={{ minWidth: 260 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visitorRequests.map((request) => (
                    <tr key={request.id}>
                      <td>{request.visitors?.[0]?.visitorCompany || 'N/A'}</td>
                      <td>
                        {request.visitors?.[0]?.arrivalTime
                          ? moment(request.visitors[0].arrivalTime).format('YYYY-MM-DD HH:mm')
                          : 'N/A'}
                      </td>
                      <td>
                        <span className={statusColors[request.status] || 'badge bg-secondary'}>
                          {request.status || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            normIn(request) === FULLY_CHECKED_IN
                              ? 'bg-success'
                              : normIn(request) === PARTIALLY_CHECKED_IN
                              ? 'bg-info'
                              : 'bg-secondary'
                          }`}
                          title={normIn(request)}
                        >
                          {normIn(request)}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`badge ${
                            normOut(request) === FULLY_CHECKED_OUT
                              ? 'bg-success'
                              : normOut(request) === PARTIALLY_CHECKED_OUT
                              ? 'bg-info'
                              : 'bg-secondary'
                          }`}
                          title={normOut(request)}
                        >
                          {normOut(request)}
                        </span>
                      </td>
                      <td>{moment(request.dateTime).format('YYYY-MM-DD HH:mm')}</td>
                      <td>
                        <div className="hstack gap-2">
                          <Button
                            variant="icon btn-secondary rounded-circle btn-sm"
                            title="View Request"
                            onClick={() => handleViewClick(request.id)}
                          >
                            <i className="feather-eye" />
                          </Button>

                          {/* Hide all action buttons if Fully Checked Out */}
                          {canShowAnyButtons(request) && (
                            <>
                              {canShowMarkIn(request) && (
                                <Button
                                  variant="success"
                                  size="sm"
                                  onClick={() => handleMarkInClick(request.id)}
                                >
                                  Mark In
                                </Button>
                              )}

                              {canShowMarkOut(request) && (
                                <Button
                                  variant="warning"
                                  size="sm"
                                  onClick={() => handleMarkOutClick(request.id)}
                                >
                                  Mark Out
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}

          <div className="d-flex justify-content-between mt-3">
            <Button
              variant="outline-primary"
              disabled={pageNo === 1}
              onClick={() => handlePageChange(pageNo - 1)}
            >
              Previous
            </Button>
            <span>Page {pageNo} of {maxPage}</span>
            <Button
              variant="outline-secondary"
              disabled={pageNo >= maxPage}
              onClick={() => handlePageChange(pageNo + 1)}
            >
              Next
            </Button>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={onHide}>Close</Button>
        </Modal.Footer>
      </Modal>

      {selectedVisitorRequestId && (
        <ViewVisitorRequest
          show={showViewModal}
          onHide={() => setShowViewModal(false)}
          visitorRequestId={selectedVisitorRequestId}
        />
      )}

      <MarkInModule
        show={showMarkInModal}
        onHide={() => setShowMarkInModal(false)}
        visitorRequestId={selectedVisitorRequestId}
        onSuccess={handleMarkChangeSuccess}
      />

      <MarkOutModule
        show={showMarkOutModal}
        onHide={() => setShowMarkOutModal(false)}
        visitorRequestId={selectedVisitorRequestId}
        onSuccess={handleMarkChangeSuccess}
      />
    </>
  );
};

export default FinalApprovedModule;
